import { useEffect, useId, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import { useI18n } from "../i18n/I18nContext";
import { cx } from "./classNames";
import { useFocusOnMount } from "./focus";
import styles from "./ui.module.css";

/**
 * Tone of the confirmation: `danger` for destructive actions (revoke, remove, end), `primary` for
 * binding actions that are not destructive (accept a contract version).
 */
export type ConfirmTone = "danger" | "primary";

/** Properties of {@link ConfirmAction}. */
export interface ConfirmActionProps {
  /** Visible label of the button that asks for the confirmation, for example „Widerrufen“. */
  readonly label: string;
  /**
   * Accessible name of that button when the visible label alone is not enough, for example
   * „API-Zugang Lohn widerrufen“; it must contain the visible label (WCAG 2.5.3).
   */
  readonly accessibleLabel?: string | undefined;
  /** One sentence about the consequence, shown in the confirmation and naming it. */
  readonly consequence: ReactNode;
  /** Label of the confirming button, for example „Endgültig widerrufen“. */
  readonly confirmLabel: string;
  /** Label of the cancelling button; defaults to „Abbrechen“. */
  readonly cancelLabel?: string | undefined;
  /** Tone; defaults to `danger`. */
  readonly tone?: ConfirmTone | undefined;
  /** Whether the action is unavailable, for example while it runs. */
  readonly disabled?: boolean | undefined;
  /**
   * Checks the content of the confirmation when the confirming button is pressed, for example a
   * required reason; returning `false` keeps the confirmation open, so the caller can show the error
   * at the field and move the focus there (F-6: the button stays available instead of being disabled
   * without saying why).
   */
  readonly canConfirm?: (() => boolean) | undefined;
  /**
   * Additional class name of the button that asks for the confirmation; it replaces the red
   * outline that a button with a red confirmation gets by default.
   */
  readonly className?: string | undefined;
  /** Further content of the confirmation, for example a field for the reason. */
  readonly children?: ReactNode;
  /** Runs the action after the confirmation. */
  readonly onConfirm: () => void;
  /** Called when the confirmation is cancelled. */
  readonly onCancel?: (() => void) | undefined;
}

/** Properties of the open confirmation. */
interface ConfirmationProps {
  readonly consequence: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly tone: ConfirmTone;
  readonly disabled: boolean;
  readonly children?: ReactNode;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * The open confirmation: a group named by the consequence that receives the focus when it opens;
 * Escape cancels.
 *
 * @param props - Component properties.
 * @returns The confirmation.
 */
function Confirmation(props: ConfirmationProps): JSX.Element {
  const panelRef = useFocusOnMount<HTMLDivElement>();
  const consequenceId = useId();
  const confirmation = (
    <div
      ref={panelRef}
      role="group"
      aria-labelledby={consequenceId}
      tabIndex={-1}
      className={cx(
        styles["alert"],
        props.tone === "danger" ? styles["alertDanger"] : styles["alertInfo"],
        styles["confirmAction"],
      )}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          props.onCancel();
        }
      }}
    >
      <p id={consequenceId}>{props.consequence}</p>
      {props.children}
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onCancel}>
          {props.cancelLabel}
        </button>
        <button
          type="button"
          className={cx(styles["button"], styles[props.tone])}
          disabled={props.disabled}
          onClick={props.onConfirm}
        >
          {props.confirmLabel}
        </button>
      </div>
    </div>
  );

  return confirmation;
}

/**
 * Button for a destructive or binding action with an inline confirmation (S-5, WCAG 3.3.4,
 * UX-113), following the sign-out with unsent entries: the first tap opens a confirmation in place
 * with a sentence about the consequence, „Abbrechen“ and the confirming button; the focus moves to
 * the confirmation, Escape or „Abbrechen“ close it and return the focus to the button. A button
 * whose confirmation is red (the default tone) is drawn with a red outline (`quietDanger`, F-30)
 * unless the caller passes its own class.
 *
 * @param props - Component properties.
 * @returns The button or the open confirmation.
 */
export function ConfirmAction(props: ConfirmActionProps): JSX.Element {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);

  useEffect(() => {
    if (!confirming && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [confirming]);

  const close = (): void => {
    restoreFocusRef.current = true;
    setConfirming(false);
  };

  if (confirming) {
    const open = (
      <Confirmation
        consequence={props.consequence}
        confirmLabel={props.confirmLabel}
        cancelLabel={props.cancelLabel ?? t("common.cancel")}
        tone={props.tone ?? "danger"}
        disabled={props.disabled === true}
        onConfirm={() => {
          if (props.canConfirm?.() === false) {
            return;
          }

          close();
          props.onConfirm();
        }}
        onCancel={() => {
          close();
          props.onCancel?.();
        }}
      >
        {props.children}
      </Confirmation>
    );

    return open;
  }

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      className={cx(
        styles["button"],
        props.className ??
          ((props.tone ?? "danger") === "danger" ? styles["quietDanger"] : undefined),
      )}
      aria-label={props.accessibleLabel}
      disabled={props.disabled}
      onClick={() => {
        setConfirming(true);
      }}
    >
      {props.label}
    </button>
  );

  return trigger;
}
